import { useNavigate } from 'react-router-dom';
import { ListTodo, CalendarClock, CheckSquare } from 'lucide-react';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card } from '../ui/Card';
import { WIDGET_HEADER_FLUSH, WidgetCardHeader } from './WidgetCardHeader';
import { Spinner } from '../ui/Spinner';
import { useCalendarEvents } from '../../hooks/queries/useCalendarEvents';
import { WidgetInfiniteList } from './WidgetInfiniteList';
import type { CalendarEvent, CalendarFilters } from '../../services/calendar/calendarAggregate';
import { fcDateToYmd } from '../../services/calendar/calendarUrlState';
import {
  revealSlice,
  selectOrdreDuJourEvents,
} from '../../services/dashboard/ordreDuJour';
import { useTaskModalStore } from '../../store/taskModalStore';

const NO_FILTERS: CalendarFilters = { lawyerIds: [], dossierId: null, kinds: [] };

function whenLabel(item: CalendarEvent, now: Date): { text: string; className: string } {
  const start = parseISO(item.start);
  const today = fcDateToYmd(now);
  const day = fcDateToYmd(start);
  const time = item.allDay ? null : format(start, 'HH:mm', { locale: fr });

  if (item.kind !== 'hearing' && day < today) {
    return { text: 'En retard', className: 'text-cime-urgent font-medium' };
  }
  if (isToday(start)) {
    return {
      text: time ?? "Aujourd'hui",
      className: time ? 'text-neutral-500 tabular-nums' : 'text-orange-600 font-medium',
    };
  }
  if (isTomorrow(start)) {
    return {
      text: time ? `Demain ${time}` : 'Demain',
      className: 'text-amber-600',
    };
  }
  const dateText = format(start, 'EEE d MMM', { locale: fr });
  return {
    text: time ? `${dateText} ${time}` : dateText,
    className: 'text-neutral-500',
  };
}

export function OrdreDuJourWidget() {
  const navigate = useNavigate();
  const openTask = useTaskModalStore((s) => s.openTask);
  const { events, isLoading, isError } = useCalendarEvents(NO_FILTERS);
  const now = new Date();
  const items = selectOrdreDuJourEvents(events, now);
  const overdueCount = items.filter((e) => {
    const today = fcDateToYmd(now);
    return e.kind !== 'hearing' && fcDateToYmd(new Date(e.start)) < today;
  }).length;
  const handleEventClick = (item: CalendarEvent) => {
    if (item.kind === 'task') {
      openTask(item.id.replace(/^task:/, ''));
    } else {
      navigate(item.href);
    }
  };

  return (
    <Card
      className="h-full"
      headerClassName={WIDGET_HEADER_FLUSH}
      header={
        <WidgetCardHeader
          icon={ListTodo}
          title="Ordre du jour"
          href="/calendrier"
          extra={
            overdueCount > 0 ? (
              <span className="text-xs font-medium text-cime-urgent shrink-0">
                {overdueCount} en retard
              </span>
            ) : null
          }
        />
      }
    >
      {isError ? (
        <p className="text-sm text-neutral-400 text-center py-3">Ordre du jour indisponible</p>
      ) : isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-3">Rien à traiter</p>
      ) : (
        <WidgetInfiniteList itemCount={items.length}>
          {(visibleCount) => {
            const page = revealSlice(items, visibleCount);
            return (
              <ul className="space-y-0.5">
                {page.items.map((item) => {
                  const when = whenLabel(item, now);
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => handleEventClick(item)}
                        className="w-full flex items-center gap-2 text-left py-1 hover:bg-neutral-50 rounded px-1 transition-colors"
                      >
                        {item.kind === 'hearing' ? (
                          <CalendarClock className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" aria-hidden />
                        ) : item.kind === 'appointment' ? (
                          <CalendarClock className="w-3.5 h-3.5 text-[#c9a84c] flex-shrink-0" aria-hidden />
                        ) : item.kind === 'task' ? (
                          <CheckSquare className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" aria-hidden />
                        ) : null}
                        <span className="text-sm text-neutral-700 flex-1 truncate">{item.title}</span>
                        <span className={`text-xs flex-shrink-0 ${when.className}`}>{when.text}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            );
          }}
        </WidgetInfiniteList>
      )}
    </Card>
  );
}
